import type { DocumentRecord } from '@/types/catalog'

export interface SortState {
  key: string
  direction: 'asc' | 'desc'
}

export interface SetGroup {
  setId: string
  setName: string
  documents: DocumentRecord[]
}

/** A catalog row is either a collapsed multi-page set or a single document. */
export type MergedRow =
  | { kind: 'set'; group: SetGroup; date: string }
  | { kind: 'doc'; doc: DocumentRecord; date: string }

/**
 * Merge collapsed set-groups and flat docs into one ordered row list.
 *
 * The previous inline sort compared dates as raw strings, hardcoded descending,
 * and ignored the active column — so the header sort did nothing and any
 * timestamp-format difference misordered rows. This mirrors the V1 catalog
 * comparator instead: parse dates numerically and honor sort.key / sort.direction.
 * A set's date is its most-recent child's `upload_started_at || created_at`.
 */
export function buildOrderedRows(
  flatDocs: DocumentRecord[],
  setGroups: SetGroup[],
  sort: SortState,
): MergedRow[] {
  const items: MergedRow[] = []

  for (const group of setGroups) {
    // Pick the most-recent child date by NUMERIC comparison — a raw string max
    // would wrongly rank '...09Z' above '...09.500Z' ('Z' > '.'), the same defect
    // this function fixes for the top-level sort.
    const latestDate = group.documents.reduce((latest, d) => {
      const dt = d.upload_started_at || d.created_at
      return new Date(dt).getTime() > new Date(latest || 0).getTime() ? dt : latest
    }, '')
    items.push({ kind: 'set', group, date: latestDate })
  }
  for (const doc of flatDocs) {
    items.push({ kind: 'doc', doc, date: doc.upload_started_at || doc.created_at })
  }

  const repDoc = (i: MergedRow): DocumentRecord | undefined =>
    i.kind === 'set' ? i.group.documents[0] : i.doc
  const nameOf = (i: MergedRow): string => (i.kind === 'set' ? i.group.setName : i.doc.name) || ''
  const timeOf = (i: MergedRow): number => {
    const ms = new Date(i.date).getTime()
    return Number.isNaN(ms) ? 0 : ms
  }

  return [...items].sort((a, b) => {
    let cmp = 0
    switch (sort.key) {
      case 'name':
        cmp = nameOf(a).localeCompare(nameOf(b))
        break
      case 'type':
        cmp = (repDoc(a)?.type ?? '').localeCompare(repDoc(b)?.type ?? '')
        break
      case 'status':
        cmp = (repDoc(a)?.status ?? '').localeCompare(repDoc(b)?.status ?? '')
        break
      case 'uploaded_by':
        cmp = (repDoc(a)?.uploader_name ?? '').localeCompare(repDoc(b)?.uploader_name ?? '')
        break
      case 'upload_started_at':
      default:
        cmp = timeOf(a) - timeOf(b)
        break
    }
    return sort.direction === 'desc' ? -cmp : cmp
  })
}
